import { trackPageContext } from "@/main/analytics/analyticsEventsCore";
import { flushQueuedAnalyticsEvents } from "@/main/analytics/navigationEventQueue";
import { refreshHeaderInbox, syncHeaderInboxFromDocument } from "@/main/headerInbox/HeaderInboxStore";
import { readShellStateFromDocument } from "@/main/navigation/ShellState";
import { applyThemeFromDocument } from "@/main/navigation/ThemeRuntime";
import { dispatchSearchStateSync, parseSearchState, readLastSentSearchState } from "@/main/react/page/search/searchState";
import { DEFAULT_SEARCH_STATE, normalizeSearchType, type SearchState } from "@/main/react/page/search/types";

type SearchStateSyncOptions = {
    url?: URL | string;
};

type PostMountDocumentEffectsOptions = SearchStateSyncOptions & {
    flushAnalytics?: boolean;
    trackPageContextEvent?: boolean;
};

function normalizeEffectsUrl(sourceDocument: Document, url?: URL | string): URL {
    const fallbackHref = typeof window !== "undefined"
        ? window.location.href
        : sourceDocument.URL || "https://example.invalid/";

    if (url instanceof URL) {
        return url;
    }

    if (typeof url === "string" && url.trim().length > 0) {
        return new URL(url, fallbackHref);
    }

    if (sourceDocument.URL && sourceDocument.URL !== "about:blank") {
        return new URL(sourceDocument.URL, fallbackHref);
    }

    return new URL(fallbackHref);
}

function getSearchStateFallback(sourceDocument: Document): Partial<SearchState> {
    const mainHeader = readShellStateFromDocument(sourceDocument).mainHeader;

    return {
        ...DEFAULT_SEARCH_STATE,
        query: mainHeader?.searchQuery ?? DEFAULT_SEARCH_STATE.query,
        type: normalizeSearchType(mainHeader?.searchType, DEFAULT_SEARCH_STATE.type),
    };
}

export function applyPreMountDocumentEffectsFromDocument(sourceDocument: Document = document): void {
    applyThemeFromDocument(sourceDocument);
}

export function syncSearchStateFromDocument(
    sourceDocument: Document = document,
    options: SearchStateSyncOptions = {},
): SearchState {
    const nextUrl = normalizeEffectsUrl(sourceDocument, options.url);
    // FI-5108 A (`EMPTY-STATES-185`): a page that already sent its state for this address is fresher than the URL
    // (`/groups` keeps its query in `q`, so the URL's `searchQuery` reads empty); do not send the URL's over it.
    const pageSent = readLastSentSearchState(nextUrl.href);
    if (pageSent && !pageSent.fromDocument) {
        return pageSent.state;
    }
    const searchState = parseSearchState(nextUrl.search, getSearchStateFallback(sourceDocument));
    dispatchSearchStateSync(searchState, true);
    return searchState;
}

export function applyPostMountDocumentEffectsFromDocument(
    sourceDocument: Document = document,
    options: PostMountDocumentEffectsOptions = {},
): SearchState {
    const searchState = syncSearchStateFromDocument(sourceDocument, options);
    const headerInboxSyncResult = syncHeaderInboxFromDocument(sourceDocument);

    if (headerInboxSyncResult.loggedIn) {
        if (!headerInboxSyncResult.hadDocumentInboxSeed) {
            void refreshHeaderInbox({ mode: "summary", reason: "document-effects-missing-seed" });
        } else if (headerInboxSyncResult.shouldForceFollowUpRefresh) {
            void refreshHeaderInbox({ mode: "summary", force: true, reason: "document-effects-reconcile" });
        }
    }

    if (options.trackPageContextEvent !== false) {
        trackPageContext("page");
    }

    if (options.flushAnalytics !== false) {
        flushQueuedAnalyticsEvents();
    }

    return searchState;
}
